import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { PURCHASE_REQUISITIONS_COLLECTION } from './purchasingRequisitions';
import { listApprovedDesignerInvoices, type DesignerInvoiceDoc } from './designerInvoiceService';
import {
  collectDesignerProjectTimeForHub,
  type DesignerHourlyRateRow,
  type DesignerInvoiceTimeGroup,
  type DesignerInvoiceTimePo,
  type DesignerQbExpenseTimeRow,
} from './designerInvoiceProjectTime';

function projectHours(invoice: DesignerInvoiceDoc, projectId: string): boolean {
  const lines = invoice.allocations?.length ? invoice.allocations : invoice.proposedLines || [];
  return lines.some((line) => {
    const hours = Number(line.hours);
    return (line.projectId || '').trim() === projectId && Number.isFinite(hours) && hours > 0;
  });
}

async function loadRequisition(id: string): Promise<DesignerInvoiceTimePo | null> {
  try {
    const snap = await getDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, id));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    const cost =
      data.costObject && typeof data.costObject === 'object'
        ? (data.costObject as Record<string, unknown>)
        : {};
    const projectId =
      typeof data.projectId === 'string' && data.projectId.trim()
        ? data.projectId.trim()
        : typeof cost.projectId === 'string'
          ? cost.projectId.trim()
          : undefined;
    return {
      id: snap.id,
      poNumber: typeof data.poNumber === 'string' ? data.poNumber : undefined,
      projectId: projectId || undefined,
      designerInvoiceId: typeof data.designerInvoiceId === 'string' ? data.designerInvoiceId : undefined,
    };
  } catch {
    return null;
  }
}

function mapQbExpenseRow(id: string, data: Record<string, unknown>): DesignerQbExpenseTimeRow {
  return {
    id,
    vendorName: typeof data.vendorName === 'string' ? data.vendorName : null,
    description: typeof data.description === 'string' ? data.description : null,
    totalAmount: Number(data.totalAmount) || 0,
    txnDate: typeof data.txnDate === 'string' ? data.txnDate : null,
    purchaseRequisitionId: typeof data.purchaseRequisitionId === 'string' ? data.purchaseRequisitionId : null,
    quickbooksId: typeof data.quickbooksId === 'string' ? data.quickbooksId : null,
    entityType: typeof data.entityType === 'string' ? data.entityType : null,
    matchedDealId: typeof data.matchedDealId === 'string' ? data.matchedDealId : null,
    splits: Array.isArray(data.splits)
      ? (data.splits as Array<{ dealId?: string; amount?: number }>).map((slice) => ({
          dealId: typeof slice.dealId === 'string' ? slice.dealId : null,
          amount: Number(slice.amount) || 0,
        }))
      : null,
  };
}

async function loadDesignerHourlyRates(vendors: string[]): Promise<DesignerHourlyRateRow[]> {
  const wanted = new Set(vendors.map(v => v.trim().toLowerCase()).filter(Boolean));
  if (!wanted.size) return [];
  try {
    const snap = await getDocs(collection(db, 'contractorProfiles'));
    const rows: DesignerHourlyRateRow[] = [];
    snap.docs.forEach(d => {
      const data = d.data() as Record<string, unknown>;
      const rate = Number(data.defaultHourlyRate);
      if (!Number.isFinite(rate) || rate <= 0) return;
      const display =
        (typeof data.displayName === 'string' && data.displayName.trim())
        || (typeof data.company === 'string' && data.company.trim())
        || d.id;
      rows.push({ userId: d.id, userName: display, hourlyRate: rate });
      const company = typeof data.company === 'string' ? data.company.trim() : '';
      if (company) rows.push({ userId: d.id, userName: company, hourlyRate: rate });
    });
    const wantedList = Array.from(wanted);
    return rows.filter(row => {
      const key = row.userName.trim().toLowerCase();
      return wantedList.some(v => key.includes(v) || v.includes(key));
    });
  } catch {
    return [];
  }
}

async function loadQbExpensesForHub(args: {
  dealId?: string | null;
  requisitionIds: string[];
}): Promise<DesignerQbExpenseTimeRow[]> {
  const byId = new Map<string, DesignerQbExpenseTimeRow>();
  const dealId = (args.dealId || '').trim();
  try {
    if (dealId) {
      const snap = await getDocs(query(collection(db, 'qbExpenses'), where('matchedDealId', '==', dealId)));
      snap.docs.forEach(d => byId.set(d.id, mapQbExpenseRow(d.id, d.data() as Record<string, unknown>)));
    }
    const reqIds = args.requisitionIds.filter(Boolean);
    for (let i = 0; i < reqIds.length; i += 10) {
      const chunk = reqIds.slice(i, i + 10);
      const snap = await getDocs(
        query(collection(db, 'qbExpenses'), where('purchaseRequisitionId', 'in', chunk)),
      );
      snap.docs.forEach(d => byId.set(d.id, mapQbExpenseRow(d.id, d.data() as Record<string, unknown>)));
    }
  } catch {
    return [];
  }
  return Array.from(byId.values());
}

export async function loadDesignerInvoiceTimeForProject(
  projectId: string,
  dealId?: string | null,
): Promise<DesignerInvoiceTimeGroup[]> {
  const id = projectId.trim();
  if (!id) return [];
  const invoices = await listApprovedDesignerInvoices();
  const relevant = invoices.filter((invoice) => projectHours(invoice, id));
  const requisitionIds = new Set<string>();
  relevant.forEach((invoice) => {
    (invoice.purchaseRequisitionIds || []).forEach((requisitionId) => requisitionIds.add(requisitionId));
  });
  const requisitions = (
    await Promise.all(Array.from(requisitionIds).map((requisitionId) => loadRequisition(requisitionId)))
  ).filter((row): row is DesignerInvoiceTimePo => !!row);

  const qbExpenses = await loadQbExpensesForHub({
    dealId,
    requisitionIds: Array.from(requisitionIds),
  });
  const hourlyRates = await loadDesignerHourlyRates(relevant.map(inv => inv.vendor || ''));

  return collectDesignerProjectTimeForHub({
    projectId: id,
    dealId,
    invoices: relevant,
    requisitions,
    qbExpenses,
    hourlyRates,
  });
}
